import React, { useState, useEffect, useRef } from "react";
import { Form, FormInstance, Input, Popover } from "antd";
import { DeleteIcon, DownArrowIcon, PlusIcon } from "../icons";
import { Alert } from "aha-components";
import PopoverContent from "./PopoverContent";
import { useTranslation } from "react-i18next";

const { TextArea } = Input;

export default function CoreAudiencesForm({
  name,
  form,
}: {
  name: string[] | string;
  form: FormInstance;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [expandedFields, setExpandedFields] = useState<Set<number>>(new Set());
  const hasInitializedRef = useRef(false);

  // 获取当前字段数量并设置默认展开
  const getCurrentFields = () => {
    const currentFields = form.getFieldValue(name);
    return currentFields || [];
  };

  // 使用 Form.useWatch 监听表单字段值变化
  const watchedFields = Form.useWatch(name, form);

  // 初始化展开状态 - 当字段值从空变为有值时初始化
  useEffect(() => {
    const currentFields = watchedFields || [];
    if (currentFields && currentFields.length > 0) {
      // 只在首次有数据时初始化，避免覆盖用户的手动操作
      if (!hasInitializedRef.current) {
        const initialExpandedFields = new Set<number>();
        currentFields.forEach((_: any, index: number) => {
          initialExpandedFields.add(index);
        });
        setExpandedFields(initialExpandedFields);
        hasInitializedRef.current = true;
      }
    }
  }, [watchedFields]);

  const toggleFieldExpansion = (index: number) => {
    const newExpandedFields = new Set(expandedFields);
    if (newExpandedFields.has(index)) {
      newExpandedFields.delete(index);
    } else {
      newExpandedFields.add(index);
    }
    setExpandedFields(newExpandedFields);
  };

  return (
    <div>
      <div className="self-stretch justify-start mb-6">
        <span className="text-font&icon-secondary text-sm font-normal font-['DM_Sans'] leading-snug">
          {t('components.coreAudiencesForm.audienceDefinitionsCritical')}{" "}
        </span>
        <Popover content={
          <PopoverContent />
        } title="" trigger="click">
          <span className="text-font&icon-link text-sm font-medium font-['DM_Sans'] underline leading-snug">
            {t('components.coreAudiencesForm.viewExamples')}
          </span>
        </Popover>
      </div>
      <div className="w-full">
        <Form.List name={name}>
          {(fields, { add, remove }) => (
            <div className="flex flex-col gap-6">
              <div className="border border-borderColor rounded-lg overflow-hidden">
                <div
                  className="bg-[#FAFAF9] h-[48px] flex items-center cursor-pointer"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <div className="w-[56px] flex justify-center items-center">
                    <DownArrowIcon
                      className={`${
                        isOpen ? "rotate-0" : "-rotate-90"
                      } transition-all duration-300`}
                    />
                  </div>
                  <div className="text-font&icon-secondary text-xs font-medium w-[32px] text-center">
                    {t('components.coreAudiencesForm.number')}
                  </div>
                  <div className="text-font&icon-secondary text-xs font-medium pl-4 flex-1">
                    {t('components.coreAudiencesForm.title')}
                  </div>
                </div>
                {isOpen && (
                  <>
                    <Alert
                      type="info"
                      description={t('components.coreAudiencesForm.audiencesPrefilledInfo')}
                      closeable
                      className="m-2"
                    />
                    {fields.map((field: any, index) => (
                      <div key={field.key} className={`w-full py-4 border-b border-borderColor ${index === fields?.length - 1 ? "border-b-0" : ""
                            }`}>
                        <div className="flex items-center pr-4">
                          <div
                            className="w-[56px] flex justify-center items-center cursor-pointer"
                            onClick={() => toggleFieldExpansion(index)}
                          >
                            <DownArrowIcon
                              className={`${
                                expandedFields.has(index) ? "rotate-0" : "-rotate-90"
                              } transition-all duration-300`}
                            />
                          </div>
                          <div className="w-[32px] text-center">
                            <span className="text-font&icon-secondary text-xs font-medium font-['DM_Sans']">
                              {index + 1}
                            </span>
                          </div>
                          <div className="flex-1 pl-4 mr-2">
                            <Form.Item
                              name={[field.name, "audience_name"]}
                                                              rules={[
                                  {
                                    required: true,
                                    message: t('components.coreAudiencesForm.pleaseEnterAudienceName'),
                                  },
                                ]}
                              className="mb-0"
                            >
                              <Input placeholder={t('components.coreAudiencesForm.enterTitle')} size="large" />
                            </Form.Item>
                          </div>
                          {fields?.length > 1 && (
                            <span
                              className="cursor-pointer w-[32px] flex justify-center items-center"
                              onClick={() => remove(field.name)}
                            >
                              <DeleteIcon className="delete" />
                            </span>
                          )}
                        </div>
                        {expandedFields.has(index) && (
                          <div
                            className={`pl-[104px] pr-14 pt-1`}
                          >
                            <Form.Item
                              name={[field.name, "audience_description"]}
                              rules={[
                                {
                                  required: true,
                                  message: t('components.coreAudiencesForm.pleaseEnterAudienceDescription'),
                                },
                              ]}
                              className="mb-0"
                            >
                              <TextArea
                                autoSize={{ minRows: 1, maxRows: 5 }}
                                size="large"
                                placeholder={t('components.coreAudiencesForm.enterDescription')}
                              />
                            </Form.Item>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
              <Form.Item className="mb-0">
                <div
                  className="flex items-center text-primary cursor-pointer"
                  onClick={() => {
                    const newIndex = fields.length;
                    add("");
                    // 新添加的字段默认展开
                    setExpandedFields(
                      (prev) => new Set([...Array.from(prev), newIndex])
                    );
                  }}
                >
                  <PlusIcon className="text-primary" />
                  <span>{t('components.coreAudiencesForm.add')}</span>
                </div>
              </Form.Item>
            </div>
          )}
        </Form.List>
      </div>
    </div>
  );
}
